import { useMemo } from 'react';
import type { ServiceProvider } from '@frontend/api-service-providers';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Table, TableColumnConfig, Avatar, TableRowActions } from '@frontend/design-system';
import { ServiceProviderSourceInfo } from './atoms';
import { ServiceProviderTableProps } from './types';

type Props = ServiceProviderTableProps & {
  tableRowActions: TableRowActions<ServiceProvider>['actions'];
};

export const ServiceProviderLargeTable = ({
  providers,
  isLoading,
  tableRowActions,
  shouldMapToExternalProviderId,
  openModal,
  integrations,
}: Props) => {
  const { t } = useTranslation('schedule');

  const tableColumns = useMemo(() => {
    const cols: TableColumnConfig<ServiceProvider>[] = [
      {
        Header: '',
        id: 'providerImage',
        sticky: 'left',
        accessor: (row: ServiceProvider) => row,
        disableSortBy: true,
        cellRenderer: (cellValue: ServiceProvider) => (
          <Avatar
            name={`${cellValue.firstName} ${cellValue.lastName}`}
            size='large'
            src={cellValue.publicDisplayImage}
          />
        ),
        width: 80,
      },
      {
        Header: t('Display Name'),
        id: 'providerName',
        accessor: 'publicDisplayName',
        disableSortBy: true,
      },
      {
        Header: t('First Name'),
        id: 'firstName',
        accessor: 'firstName',
        disableSortBy: true,
      },
      {
        Header: t('Last Name'),
        id: 'lastName',
        accessor: 'lastName',
        disableSortBy: true,
      },
      {
        Header: t('Source'),
        id: 'resourceName',
        accessor: 'sourceId',
        disableSortBy: true,
        cellRenderer: (cellValue: string) => (
          <ServiceProviderSourceInfo
            sourceId={cellValue}
            integrations={integrations}
            shouldMapToExternalProviderId={shouldMapToExternalProviderId}
            openModal={openModal}
          />
        ),
      },
      {
        Header: t('Updated On'),
        accessor: 'updatedAt',
        id: 'updatedAt',
        disableSortBy: true,
        cellRenderer: (cellValue: string) => {
          return cellValue ? new Date(cellValue).toLocaleString('en-US') : '';
        },
      },
    ];
    return cols;
  }, [integrations, shouldMapToExternalProviderId, openModal, t]);

  return (
    <>
      <Table
        fullHeight
        tableStyle={{
          name: 'provider-table',
          styles: `margin-top: ${theme.spacing(3)};`,
        }}
        colConfig={tableColumns}
        data={providers ?? []}
        isLoading={isLoading}
        emptyStateConfig={{
          type: 'schedule',
        }}
        hasResponsiveColWidths
        rowActions={{
          actions: tableRowActions,
        }}
        hasGlobalSearch
        isPaginated
        clientPaginationConfig={{
          rowsPerPageOptions: [10, 25, 50, 75, 100],
          defaultRowsPerPage: 50,
        }}
      />
    </>
  );
};
